import debounce from 'lodash/debounce'
import customDropdown from '@/components/dropdown/customDropdown.vue'
import TableButton from '../../../@core/components/buttons/TableButton.vue'
import AddEditShift from '../../../views/masterSettings/components/AddEditShift.vue'
import ViewShift from '../../../views/masterSettings/components/ViewShift.vue'

export default {
  name: 'ShiftManagement',
  components: {
    TableButton,
    customDropdown,
    AddEditShift,
    ViewShift,
  },
  data() {
    return {
      search: null,
      is_active: 'active',
      totalCount: 0,
      currentPage: 1,
      perPage: 10,
      loader: true,
      sortBy: 'name',
      sortDesc: false,
      shifts: [],
      fields: [
        {
          key: 'name',
          label: 'Shift Name',
          sortable: true,
          thStyle: { minWidth: '150px' },
        },
        {
          key: 'code',
          label: 'Shift Code',
          sortable: true,
          thStyle: { minWidth: '120px' },
        },
        {
          key: 'start_time',
          label: 'Start Time',
          sortable: false,
          thStyle: { minWidth: '110px' },
        },
        {
          key: 'end_time',
          label: 'End Time',
          sortable: false,
          thStyle: { minWidth: '110px' },
        },
        {
          key: 'total_working_hours',
          label: 'Working Hrs',
          sortable: false,
          thStyle: { minWidth: '120px' },
        },
        {
          key: 'working_days',
          label: 'Working Days',
          sortable: false,
          thStyle: { minWidth: '260px' },
        },
        {
          key: 'weekly_off_days',
          label: 'Weekly Off Days',
          sortable: false,
          thStyle: { minWidth: '200px' },
        },
        {
          key: 'grace_minutes',
          label: 'Grace Period',
          sortable: false,
          thStyle: { minWidth: '120px' },
        },
        {
          key: 'lunch_break_minutes',
          label: 'Lunch Break',
          sortable: false,
          thStyle: { minWidth: '110px' },
        },
        {
          key: 'is_active',
          label: 'Active',
          sortable: false,
          thStyle: { minWidth: '60px' },
        },
        {
          key: 'created_by',
          label: 'Created By',
          sortable: false,
          thStyle: { minWidth: '180px' },
        },
        {
          key: 'action',
          label: 'Action',
          thClass: 'text-center',
          tdClass: 'text-center',
          sortable: false,
          thStyle: { minWidth: '90px' },
        },
      ],
    }
  },
  computed: {
    debouncedSearch: {
      get() {
        return this.search
      },
      set: debounce(function (value) {
        this.search = value
        this.getShiftList()
      }, 750),
    },
  },
  watch: {
    perPage() {
      if (this.currentPage === 1) {
        this.getShiftList()
      } else {
        this.currentPage = 1
      }
    },
    currentPage: {
      handler(value) {
        if (value) {
          this.getShiftList()
        }
      },
    },
  },
  mounted() {
    this.getShiftList()
  },
  methods: {
    /**
     * Fetch shift list from API
     * @method POST
     * @returns shifts list
     */
    async getShiftList() {
      this.loader = true
      const input = {
        page: this.currentPage,
        per_page: this.perPage,
        sort_field: this.sortBy,
        sort_order: this.sortDesc ? 'desc' : 'asc',
        search: this.search,
        is_active:
          this.is_active && this.is_active !== 'all_data'
            ? this.is_active === 'active'
            : '',
      }
      const response = await this.getHTTPPostResponse(
        'admin/master/shift/list',
        input,
        false,
      )
      if (response && response.data) {
        const { data } = response
        this.shifts = data.shifts || []
        this.totalCount = data.count || 0
      }
      this.loader = false
    },

    /** Open Add Shift sidebar */
    openAddShift() {
      this.$refs.addEditShift.open()
    },

    /** Open Edit Shift sidebar */
    openEditShift(item) {
      this.$refs.addEditShift.open(item)
    },

    /** Open View Shift sidebar */
    openViewShift(item) {
      this.$refs.viewShift.open(item)
    },

    /**
     * Normalise status field — API returns is_active: 1 or 0
     * @param {Object} item shift record
     * @returns {Boolean}
     */
    isShiftActive(item) {
      return item.is_active === 1 || item.is_active === true
    },

    /**
     * Toggle switch handler — shows SweetAlert confirm then calls API.
     * Reverts the toggle visually if the user cancels (same pattern as Tag module).
     * @param {Object} item shift record
     */
    async changeStatus(item) {
      const willBeActive = !this.isShiftActive(item)
      const label = willBeActive ? 'Activate' : 'Deactivate'
      const title = `Are you sure you want to ${label} this shift?`

      const confirmation = await this.conformationAlert(false, title, label)

      if (confirmation.isConfirmed === true) {
        const response = await this.getHTTPPutResponse(
          `admin/master/shift/status/${item.id}`,
          { is_active: willBeActive ? 1 : 0 },
          true,
        )
        if (response && response.status === 200) {
          this.getShiftList()
        } else {
          // revert on API failure
          this.getShiftList()
        }
      } else {
        // user cancelled — reload to revert the visual toggle
        this.getShiftList()
      }
    },

    /**
     * Convert day number array → short day name chips (Mon, Tue…)
     * @param {Array} days array of day numbers (1=Mon … 7=Sun)
     * @returns {Array} array of short day name strings
     */
    getDayChips(days) {
      if (!days || !days.length) return []
      const dayMap = {
        1: 'Mon',
        2: 'Tue',
        3: 'Wed',
        4: 'Thu',
        5: 'Fri',
        6: 'Sat',
        7: 'Sun',
      }
      return days.map(d => dayMap[d] || d)
    },

    /** Clear all filters */
    clearFilter() {
      this.search = null
      this.is_active = 'active'
      this.perPage = 10
      this.currentPage = 1
      this.getShiftList()
    },
  },
}
